import { createI18n } from "vue-i18n";
import en from "../locales/en.json";
import id from "../locales/id.json";

// Define supported languages
export const SUPPORTED_LOCALES = ["en", "id"] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

// Default language is Indonesian (id)
export const DEFAULT_LOCALE: SupportedLocale = "id";

// Language display names
export const LANGUAGE_NAMES: Record<SupportedLocale, string> = {
    en: "EN",
    id: "ID",
};

// Create i18n instance
const i18n = createI18n({
    legacy: false, // Use Composition API mode
    locale: DEFAULT_LOCALE, // Default locale
    fallbackLocale: "id", // Fallback to Indonesian if translation missing
    messages: {
        en,
        id,
    },
    // Global injection for $t in templates
    globalInjection: true,
    // Missing translation handler
    missing: (locale, key) => {
        if (import.meta.env.DEV) {
            console.warn(
                `[i18n] Missing translation for key "${key}" in locale "${locale}"`,
            );
        }
        return key;
    },
});

export default i18n;

// Helper function to get current locale
export const getCurrentLocale = (): SupportedLocale => {
    return i18n.global.locale.value as SupportedLocale;
};

// Helper function to set locale
export const setLocale = (locale: SupportedLocale) => {
    if (!SUPPORTED_LOCALES.includes(locale)) {
        console.warn(
            `[i18n] Unsupported locale: "${locale}". Falling back to "${DEFAULT_LOCALE}".`,
        );
        locale = DEFAULT_LOCALE;
    }
    i18n.global.locale.value = locale;
};

// Helper function to get available locales
export const getAvailableLocales = (): SupportedLocale[] => {
    return [...SUPPORTED_LOCALES];
};

// Helper function to get language display name
export const getLanguageName = (locale: SupportedLocale): string => {
    return LANGUAGE_NAMES[locale] || locale.toUpperCase();
};
